import type { ConcealRule } from "./body.ts";
import { inPresenceArea, type PresenceArea } from "./presence-area.ts";

/**
 * Whether the viewer can see a remote entity (ADR-061's amendment for P1.9-T73, R-0115): the client's judgement, made
 * each frame, of whether another player's character or a creature is concealed from its own viewer, by a prop drawn
 * over most of its body or by a roof the viewer is not under, and whether the world UI drawn for it hides. Cosmetic
 * only: the entity is still in the snapshot.
 */

/**
 * The next concealment of an entity: concealed while it stands under a roof the viewer is not under, and otherwise by
 * the coverage the prop hiding most of its body gives, with the rule's hysteresis: a shown entity is concealed at
 * `hideAt` or more, and a concealed one shows again only below `showBelow`.
 *
 * @param was Whether the entity was concealed at its last judgement.
 * @param coverage The largest share of its body box one prop drawn above it hides, in `[0, 1]`.
 * @param roofed Whether it stands under a roof the viewer is not under (`roofedApart`).
 * @param rule The hysteresis.
 * @returns Whether it is concealed now.
 */
export function concealedNext(was: boolean, coverage: number, roofed: boolean, rule: ConcealRule): boolean {
  if (roofed) {
    return true;
  }
  return coverage >= (was ? rule.showBelow : rule.hideAt);
}

/**
 * The share of an entity's body that counts toward its concealment: in the local character's cone of sight only the
 * cover a prop the cone never dims gives, a building part or a thing a player uses (ADR-061's amendment for P1.9-T75,
 * decision 5, as the author's rulings of 2026-10-03 narrow it), so a player behind a house wall or a well ahead stays
 * concealed while one behind a tree ahead is seen; outside the cone the cover of whichever prop hides most.
 *
 * @param coverage The largest share of its body one prop drawn above it hides.
 * @param keptCoverage The largest share one prop the cone never dims, drawn above it, hides.
 * @param sighted Whether its feet stand in the cone of sight.
 * @returns The share `concealedNext` judges by.
 */
export function concealingShare(coverage: number, keptCoverage: number, sighted: boolean): number {
  return sighted ? keptCoverage : coverage;
}

/**
 * Whether the world UI drawn for an entity hides: it is concealed and the viewer has not targeted it, so a target is
 * never lost mid-fight.
 *
 * @param concealed Whether the entity is concealed (`concealedNext`).
 * @param targeted Whether it is the viewer's target.
 * @returns True when its plate, bar, bubble and numbers hide.
 */
export function worldUiHidden(concealed: boolean, targeted: boolean): boolean {
  return concealed && !targeted;
}

/**
 * Whether an entity stands under a roof the viewer is not under: its feet box lies in some presence area that does
 * not hold the viewer's own character, by the inside test the roof opens by.
 *
 * @param areas Every presence area of the world.
 * @param viewerInside For each area by index, nonzero when the viewer's own character stands in it.
 * @param x The entity's feet x in sixteenths.
 * @param y The entity's feet line in sixteenths.
 * @returns True when a roof the viewer is not under covers the entity.
 */
export function roofedApart(areas: readonly PresenceArea[], viewerInside: ArrayLike<number>, x: number, y: number): boolean {
  for (let index = 0; index < areas.length; index += 1) {
    if (viewerInside[index] === 0 && inPresenceArea(areas[index] as PresenceArea, x, y)) {
      return true;
    }
  }
  return false;
}
